/**
 * cluster — a run of floats that could not fit, collapsed into one card.
 *
 * Same contract as `float`: screen pixels around its own origin, placed and
 * scaled by the scene's slot. A fan of up to three avatars, a "+N" bead that
 * continues the fan, and the shared label underneath.
 */
import type { ClusterCtx, Fragment } from '../types';
import { n, svgEl } from '../../core/svg';
import { avatarDisc, ellipsize } from './avatar';
import { FLOAT } from './metrics';

const W = FLOAT.clusterWidth;
const H = FLOAT.clusterHeight;
const AVATAR_R = 13;
/**
 * How far right of the last disc each one sits.
 *
 * It used to be 15px against a 26px disc, which buried 11px of every covered
 * avatar and left one letter of a two-letter monogram showing. At 1.5 times the
 * radius the overlap is 6.5px, so a covered disc still shows both its initials
 * — centred initials are about 11px wide — and the fan still reads as a stack.
 */
const STEP = AVATAR_R * 1.5;
/** Gap ring drawn behind each disc, so one edge reads clear of the next. */
const FAN_GAP = 1.4;
export const MAX_AVATARS = 3;
const LABEL_MAX = 26;

export const clusterFragment: Fragment<ClusterCtx> = (ctx, state) => {
  const hot = state.hover || state.selected;
  const g = svgEl('g', { 'data-frag': 'cluster' });

  if (hot) {
    g.append(
      svgEl('rect', {
        x: n(-W / 2 - 3),
        y: n(-H / 2 - 3),
        width: n(W + 6),
        height: n(H + 6),
        rx: 13,
        fill: 'none',
        stroke: 'var(--accent)',
        'stroke-width': 1,
        opacity: 0.32,
        'pointer-events': 'none',
      }),
    );
  }

  g.append(
    svgEl('rect', {
      x: n(-W / 2),
      y: n(-H / 2),
      width: n(W),
      height: n(H),
      rx: 10,
      fill: hot ? 'var(--float-bg-hover)' : 'var(--cluster-bg)',
      stroke: hot ? 'var(--accent)' : 'var(--float-border)',
      'stroke-width': state.selected ? 1.6 : 1,
      'pointer-events': 'fill',
    }),
  );

  const shown = ctx.memberEvents.slice(0, MAX_AVATARS);
  const left = -W / 2 + 9 + AVATAR_R;
  const cy = -H / 2 + 4 + AVATAR_R;

  // Left to right, in date order: each disc overlaps the *left* edge of the one
  // after it, so every avatar keeps its own centre — and its initials — clear.
  for (let i = 0; i < shown.length; i++) {
    const cx = left + i * STEP;
    g.append(
      svgEl('circle', {
        cx: n(cx),
        cy: n(cy),
        r: n(AVATAR_R + FAN_GAP),
        fill: hot ? 'var(--float-bg-hover)' : 'var(--cluster-bg)',
        'pointer-events': 'none',
      }),
    );
    g.append(
      avatarDisc({
        cx,
        cy,
        r: AVATAR_R,
        event: shown[i]!,
        colour: ctx.colours?.[i] ?? 'var(--accent)',
        ring: 1,
        fontSize: 10,
      }),
    );
  }

  // Appended after the fan, so the bead that says how many are left over is the
  // last thing drawn and nothing can cover it.
  if (ctx.hiddenCount > 0) {
    const label = `+${ctx.hiddenCount}`;
    const pillW = 16 + 6.5 * label.length;
    const pillX = left + (shown.length - 1) * STEP + AVATAR_R + 5;
    g.append(
      svgEl('rect', {
        x: n(pillX),
        y: n(cy - 9),
        width: n(pillW),
        height: 18,
        rx: 9,
        fill: 'var(--pill-bg)',
        'pointer-events': 'none',
      }),
    );
    const text = svgEl('text', {
      x: n(pillX + pillW / 2),
      y: n(cy + 4),
      'text-anchor': 'middle',
      'font-size': 10.5,
      'font-weight': 600,
      fill: 'var(--text)',
      'pointer-events': 'none',
    });
    text.textContent = label;
    g.append(text);
  }

  const caption = svgEl('text', {
    x: n(-W / 2 + 10),
    y: n(H / 2 - 7),
    'font-size': 9.5,
    'letter-spacing': 0.3,
    fill: 'var(--text-dim)',
    'pointer-events': 'none',
  });
  caption.textContent = ellipsize(ctx.label, LABEL_MAX);
  g.append(caption);

  return g;
};
